import { useSyncActivity } from "@/lib/sync-activity-context";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { useLocation } from "wouter";

export function SyncBanner() {
  const { isSyncActive, activeSyncInfo, syncJustCompleted, dismissCompletion } = useSyncActivity();
  const [, setLocation] = useLocation();

  if (!isSyncActive && !syncJustCompleted) return null;

  const handleClick = () => {
    if (syncJustCompleted) {
      dismissCompletion();
    }
    setLocation("/sync");
  };

  if (isSyncActive) {
    const directionLabel = activeSyncInfo?.direction === "push" ? "pushing to" : "syncing with";
    const adapterLabel = activeSyncInfo?.adapterName || "your accounting software";
    const entityPhase = activeSyncInfo?.currentEntityPhase;
    const phaseLabel = entityPhase ? ` — syncing ${entityPhase}s\u2026` : "";
    const message = `We're ${directionLabel} ${adapterLabel} now${phaseLabel}`;

    return (
      <div
        onClick={handleClick}
        className="relative flex items-center justify-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-foreground/5 border-b border-border cursor-pointer hover:bg-foreground/8 transition-colors"
        data-testid="banner-sync-active"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") handleClick(); }}
      >
        <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground animate-spin" />
        <span className="truncate" data-testid="text-sync-active-message">{message}</span>
        <span className="text-[10px] text-muted-foreground hidden sm:inline">Click to view details</span>
      </div>
    );
  }

  if (syncJustCompleted) {
    const { adapterName, recordsProcessed, recordsFailed, hasErrors } = syncJustCompleted;
    const successCount = recordsProcessed - recordsFailed;
    let message: string;
    if (hasErrors && recordsFailed > 0) {
      message = `Sync complete — ${successCount} record${successCount !== 1 ? "s" : ""} synced, ${recordsFailed} need${recordsFailed !== 1 ? "" : "s"} your attention`;
    } else if (recordsProcessed > 0) {
      message = `Your data is up to date — ${recordsProcessed} record${recordsProcessed !== 1 ? "s" : ""} synced with ${adapterName}`;
    } else {
      message = `Sync complete — everything is up to date with ${adapterName}`;
    }

    return (
      <div
        onClick={handleClick}
        className="relative flex items-center justify-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-healthy/10 border-b border-healthy/20 cursor-pointer hover:bg-healthy/15 transition-colors"
        data-testid="banner-sync-complete"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") handleClick(); }}
      >
        {hasErrors ? (
          <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-degraded" />
        ) : (
          <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-healthy" />
        )}
        <span className="truncate" data-testid="text-sync-complete-message">{message}</span>
      </div>
    );
  }

  return null;
}
